import { useState } from 'react';
import { useCreate, useNotify } from 'ra-core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { lireLErreur } from '../../fournisseurs/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **CRÉER UNE STRUCTURE — F-56, ET LE SECRET NE PARAÎT QU'ICI.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-56 : « **le compte qui voit tout crée une structure, seul, et le geste laisse sa
 * trace.** » Et : « **LA CRÉATION D'UNE STRUCTURE PASSE PAR CETTE SURFACE, ET PAR ELLE
 * SEULE** (Q566). `CDC-1` portait un second chemin ; **il est retiré**. `CDC-1` dit **le
 * droit**, ce document dit **le chemin**. »
 *
 * **Un seul champ, et c'est la liste fermée qui le veut.** Le nom interne, rien d'autre. « Les
 * cinq réglages naissent avec la structure, **chacun portant sa valeur par défaut écrite à la
 * création** : aucune valeur "héritée" ni "non définie" n'existe » — donc rien à saisir. Les
 * six colonnes d'affichage se remplissent ensuite, sur la fiche.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CE N'EST PAS UN `<Create>` DE LA PEAU, ET C'EST LA SEULE RAISON.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `<Create>` enregistre puis **redirige** vers la fiche ou la liste. Ici, la réponse porte
 * **une valeur qui n'existera plus jamais** : F-57, « il est émis par le geste qui crée la
 * structure, **affiché une seule fois et jamais réaffiché** », la base n'en portant que
 * l'empreinte (Q537).
 *
 * **Une redirection perdrait le secret**, définitivement, et la seule réparation serait de le
 * régénérer — ce qui coupe les appels de la structure sans période de recouvrement et sans
 * email (cas de bord 5). L'écran reste donc sur place et **c'est la personne qui décide de
 * partir**, une fois la valeur copiée.
 *
 * `DOCTRINE.md` §11 nomme cette sortie et l'autorise d'avance : « **une action métier custom
 * dans l'admin n'est pas du CRUD et n'a pas à y entrer.** » Et §7 : « **on ne cherche jamais à
 * faire entrer une action complexe dans un formulaire de ressource.** »
 */
export function CreationDeStructure() {
  const [creer] = useCreate();
  const notifier = useNotify();
  const [nomInterne, setNomInterne] = useState('');
  const [emise, setEmise] = useState<{ structureId: string; secret: string } | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    const nom = nomInterne.trim();
    if (nom === '') {
      notifier('Le nom interne ne peut pas être vide.', { type: 'warning' });
      return;
    }
    setEnCours(true);
    // `useCreate` rend une promesse que les rappels ci-dessous traitent déjà : le `void` dit
    // au lint que l'absence de `await` est délibérée, et non un oubli.
    void creer(
      'structures',
      { data: { nomInterne: nom } },
      {
        onSuccess: (creee: { id: string; secret?: string }) => {
          setEmise({ structureId: creee.id, secret: creee.secret ?? '' });
          setNomInterne('');
          setEnCours(false);
        },
        onError: (erreur: unknown) => {
          notifier(lireLErreur(erreur).message, { type: 'error' });
          setEnCours(false);
        },
      },
    );
  };

  return (
    <main className="flex max-w-2xl flex-col gap-6 p-6">
      <h2 className="text-lg font-medium">Créer une structure</h2>

      {emise !== null && (
        <Alert>
          <AlertTitle>
            La structure est créée. Voici son secret d’appel — il ne sera plus jamais affiché.
          </AlertTitle>
          <AlertDescription>
            <code className="mt-1 block break-all font-mono text-xs">{emise.secret}</code>
            <span className="mt-2 block">
              Copiez-le maintenant et transmettez-le hors du produit : aucun email ne part. La
              base n’en porte que l’empreinte, donc personne ne peut le relire — le perdre
              oblige à régénérer, ce qui coupe immédiatement les appels de la structure.
            </span>
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-col gap-3" onSubmit={soumettre}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="nomInterne">Nom interne</Label>
          <Input
            id="nomInterne"
            value={nomInterne}
            onChange={(e) => setNomInterne(e.target.value)}
            autoComplete="off"
          />
          <span className="text-muted-foreground text-xs">
            La seule désignation qu’un compte d’administration emploie. Aucun champ
            administratif, aucune personne de contact, aucune note libre : la liste des champs
            d’une structure est fermée.
          </span>
        </div>
        <Button type="submit" disabled={enCours} className="self-start">
          Créer et émettre le secret
        </Button>
      </form>
    </main>
  );
}
